(() => {
  'use strict';

  const MODE_LABELS = {
    primary: 'Blue Light',
    readers: 'Readers',
    prescription: 'Prescription'
  };

  const normalize = (value) => String(value || '').trim();

  class BuildPair extends HTMLElement {
    connectedCallback() {
      if (this._connectionScheduled) return;
      this._connectionScheduled = true;

      const start = () => this.initialize(0);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
      } else {
        window.queueMicrotask(start);
      }
    }

    initialize(attempt) {
      if (this._initialized) return;

      this.drawer = this.closest('x-drawer');
      this.formId = this.dataset.formId;
      this.form = document.getElementById(this.formId);
      this.productInfo = this.form?.closest('.product-info') || null;
      this.builderRoot = this.querySelector('lb-lens-builder');
      this.builder = this.builderRoot?.lbController || null;
      this.product = this.builder?.product || this.parseProduct();
      this.colorOptionPosition = Number(this.dataset.colorOptionPosition || 0);
      this.lensOptionPosition = Number(this.dataset.lensOptionPosition || 0);
      this.colorOptionIndex = this.colorOptionPosition - 1;
      this.lensOptionIndex = this.lensOptionPosition - 1;
      this.primaryLensValue = this.dataset.primaryLensValue;
      this.rxLensValue = this.dataset.rxLensValue;

      const picker = this.currentPicker();
      const hasCombinedProductInputs = Boolean(picker?.querySelector('input[data-product-url]'));
      const ready = this.drawer && this.form && this.productInfo && this.builder && this.product && picker
        && this.colorOptionIndex >= 0 && this.lensOptionIndex >= 0 && !hasCombinedProductInputs;

      if (!ready) {
        if (attempt < 20 && !hasCombinedProductInputs) {
          window.setTimeout(() => this.initialize(attempt + 1), 50);
        }
        return;
      }

      this._initialized = true;
      this.syncPreviewBarOffset();
      this.previewBarObserver = new MutationObserver(() => this.syncPreviewBarOffset());
      this.previewBarObserver.observe(document.body, { childList: true });
      this.step = 1;
      this.pending = false;
      this.cartHandoff = false;
      this.committedThisOpen = false;
      this.openRevision = 0;

      this.nextButton = this.drawer.querySelector('[data-lb-pair-next]');
      this.status = this.drawer.querySelector('[data-lb-pair-status]');
      this.configuration = this.querySelector('[data-lb-pair-configuration]');

      this.bindEvents();
      this.refreshPdp();
      this.syncStagedSelection({ preferRequested: true });
      this.setStage(1, { focus: false });
      this.reconcileRequestedMode();
    }

    syncPreviewBarOffset() {
      this.drawer?.classList.toggle('lb-build-pair-drawer--preview-offset', Boolean(document.getElementById('PBarNextFrame')));
    }

    parseProduct() {
      const script = this.builderRoot?.querySelector('[data-lb-product]');
      if (!script) return null;

      try {
        return JSON.parse(script.textContent || '{}');
      } catch (error) {
        return null;
      }
    }

    bindEvents() {
      this.drawer.addEventListener('dialog:before-show', () => this.onOpen());
      this.drawer.addEventListener('dialog:after-show', () => this.onAfterShow());
      this.drawer.addEventListener('dialog:before-hide', () => this.onBeforeHide());
      this.drawer.addEventListener('dialog:after-hide', () => this.onAfterHide());

      this.drawer.addEventListener('click', (event) => {
        if (event.target.closest('[data-lb-pair-next]')) {
          this.goNext();
          return;
        }

        if (event.target.closest('[data-lb-pair-change]')) {
          this.leaveConfiguration();
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-lb-pair-lens]')) {
          if (event.target.disabled) return;
          this.stagedMode = event.target.value;
          this.updatePreview();
          this.updateActions();
        }
      });

      this.form.addEventListener('change', (event) => {
        const control = event.target;
        if (!control.matches?.('input[data-option-position]')
          || Number(control.dataset.optionPosition) !== this.colorOptionPosition) return;

        const color = this.nativeOptionLabel(control);
        if (!color) return;

        this.stagedColor = color;
        this.stagedColorOptionValueId = control.value;
        const selectedLabel = this.currentPicker()?.querySelector('.lb-selected-option');
        if (selectedLabel) selectedLabel.textContent = color;
        this.updateLensAvailability({ chooseFallback: true });
        this.updatePdpSummary(color, this.stagedMode || 'primary');
        this.updatePdpControls(color, this.stagedMode || 'primary');
      });

      this.form.addEventListener('variant:change', () => {
        window.setTimeout(() => {
          this.refreshPdp();
          this.syncStagedSelection({ preserveMode: true });
        }, 0);
      });

      this.form.addEventListener('variant:error', () => {
        window.setTimeout(() => {
          this.refreshPdp();
          this.syncStagedSelection({ preserveMode: true });
        }, 0);
      });

      this.form.addEventListener('cart:error', (event) => {
        if (!this.pending) return;
        this.setBusy(false);
        const message = event.detail?.error || 'This pair could not be added.';
        this.setStatus(message, true);
        this.setPdpStatus(message, true);
      });

      document.addEventListener('variant:add', (event) => {
        if (event.target !== this.form) return;

        if (!this.drawer.open) {
          if (this.pending) this.setBusy(false);
          return;
        }

        this.cartHandoff = true;
        this.setBusy(false);
        this.suppressFocusReturn();
        this.drawer.hide();
      }, true);
    }

    onOpen() {
      this.openRevision += 1;
      this.cartHandoff = false;
      this.committedThisOpen = false;
      this.setBusy(false);
      this.setStatus('');
      this.clearBuilderFeedback();
      this.hideConfiguration();
      this.syncStagedSelection({ preserveMode: true });

      if (['readers', 'prescription'].includes(this.stagedMode)) {
        this.showConfiguration({ color: this.stagedColor, mode: this.stagedMode }, { focus: false });
      } else {
        this.setStage(1, { focus: false });
      }

      this.updatePdpControls();

      const body = this.drawer.shadowRoot?.querySelector('[part="body"]');
      body?.scrollTo({ top: 0, left: 0 });
    }

    onAfterShow() {
      const focusTarget = this.configuration && !this.configuration.hidden
        ? this.querySelector('[data-lb-pair-configuration-title]')
        : this.querySelector('[data-lb-pair-lens]:checked:not(:disabled)');
      window.setTimeout(() => {
        if (this.drawer?.hasAttribute('open')) focusTarget?.focus({ preventScroll: true });
      }, 0);
    }

    onBeforeHide() {
      this.openRevision += 1;
      if (this.cartHandoff || this.committedThisOpen) this.suppressFocusReturn();
    }

    onAfterHide() {
      this.hideConfiguration();
      if (!this.pending) this.setBusy(false);
      this.updatePdpControls();

      if (this.cartHandoff) {
        this.cartHandoff = false;
        return;
      }

      window.setTimeout(() => this.currentTrigger()?.focus({ preventScroll: true }), 0);
    }

    suppressFocusReturn() {
      try {
        this.drawer.focusTrap?.deactivate({ returnFocus: false });
      } catch (error) {
        // The theme drawer may already be between focus states; hide still completes safely.
      }
    }

    clearBuilderFeedback() {
      const status = this.builderRoot?.querySelector('[data-lb-status]');
      if (status) status.innerHTML = '';
      this.builder?.updateSummaryDock?.();
    }

    currentTriggers() {
      if (!this.drawer?.id) return [];
      const explicitTriggers = Array.from(document.querySelectorAll(
        `[data-lb-pair-trigger][data-lb-pair-drawer-id="${CSS.escape(this.drawer.id)}"]`
      ));
      if (explicitTriggers.length) return explicitTriggers;

      return Array.from(document.querySelectorAll(`[aria-controls="${CSS.escape(this.drawer.id)}"]`))
        .map((control) => control.closest('[data-lb-pair-trigger]'))
        .filter((trigger, index, triggers) => trigger && triggers.indexOf(trigger) === index);
    }

    currentTrigger() {
      const trigger = this.currentTriggers()[0];
      if (!trigger) return null;
      return trigger.querySelector(
        `[data-lb-pair-pdp-action], [aria-controls="${CSS.escape(this.drawer.id)}"]`
      );
    }

    bindPdpTrigger(trigger) {
      if (trigger.dataset.lbPairBound === 'true') return;
      trigger.dataset.lbPairBound = 'true';

      trigger.addEventListener('change', (event) => {
        const input = event.target;
        if (!input.matches?.('[data-lb-pair-pdp-lens]') || input.disabled) return;
        this.selectPdpMode(input.value);
      });

      const action = trigger.querySelector('[data-lb-pair-pdp-action]');
      action?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.handlePdpAction();
      });
    }

    refreshPdp() {
      this.productInfo = this.form.closest('.product-info') || this.productInfo;
      if (!this.productInfo) return;

      this.productInfo.classList.add('lb-build-pair-enabled');
      this.currentPicker()?.querySelector('.variant-picker__option--lenses')?.classList.add('lb-build-pair__native-lens-option');
      this.currentTriggers().forEach((trigger) => {
        this.bindPdpTrigger(trigger);
        trigger.hidden = false;
      });

      document.querySelectorAll(`product-sticky-bar[form="${CSS.escape(this.formId)}"]`).forEach((bar) => {
        bar.hidden = true;
      });

      const variant = this.currentVariant();
      const colorControl = this.nativeOptionControl(this.colorOptionPosition);
      const color = this.nativeOptionLabel(colorControl) || this.variantOptions(variant)[this.colorOptionIndex] || this.stagedColor || '';
      const lens = this.variantOptions(variant)[this.lensOptionIndex];
      const selectedLabel = this.currentPicker()?.querySelector('.lb-selected-option');
      if (selectedLabel && color) selectedLabel.textContent = color;
      const mode = lens === this.rxLensValue && ['readers', 'prescription'].includes(this.builder.mode)
        ? this.builder.mode
        : 'primary';
      if (!this.stagedColor && color) this.stagedColor = color;
      this.updatePdpSummary(color, mode);
      this.updatePdpControls(color, this.stagedMode || mode);
    }

    updatePdpSummary(color, mode) {
      const summary = `${color} · ${this.modeLabel(mode)}`;
      this.currentTriggers().forEach((trigger) => {
        const summaryElement = trigger?.querySelector('[data-lb-pair-pdp-summary]');
        if (summaryElement) summaryElement.textContent = summary;
      });
    }

    updatePdpControls(color = this.stagedColor, mode = this.stagedMode || 'primary') {
      const activeMode = ['primary', 'readers', 'prescription'].includes(mode) ? mode : 'primary';
      const modeIsAvailable = this.stagedColor ? this.modeAvailable(activeMode) : true;

      this.currentTriggers().forEach((trigger) => {
        trigger.querySelectorAll('[data-lb-pair-pdp-lens]').forEach((input) => {
          const available = this.stagedColor ? this.modeAvailable(input.value) : true;
          input.checked = input.value === activeMode;
          input.disabled = this.pending || !available;
          const choice = input.closest('.lb-build-pair-trigger__lens-choice');
          choice?.classList.toggle('is-selected', input.checked);
          choice?.classList.toggle('is-disabled', !available);
        });

        const action = trigger.querySelector('[data-lb-pair-pdp-action]');
        if (action) {
          const opensDrawer = activeMode !== 'primary';
          action.textContent = opensDrawer ? 'BUILD MY PAIR' : 'ADD TO CART';
          action.disabled = this.pending || !modeIsAvailable;
          action.toggleAttribute('aria-busy', this.pending);
          action.classList.toggle('button--outline', !opensDrawer);

          if (opensDrawer) {
            action.setAttribute('aria-controls', this.drawer.id);
            action.setAttribute('aria-haspopup', 'dialog');
            action.setAttribute('aria-expanded', String(Boolean(this.drawer?.hasAttribute('open'))));
          } else {
            action.removeAttribute('aria-controls');
            action.removeAttribute('aria-haspopup');
            action.removeAttribute('aria-expanded');
          }
        }

        if (color) {
          const summary = trigger.querySelector('[data-lb-pair-pdp-summary]');
          if (summary) summary.textContent = `${color} · ${this.modeLabel(activeMode)}`;
        }
      });

      this.syncPaymentGate(color, activeMode);
    }

    syncPaymentGate(color, mode) {
      const gate = this.productInfo?.querySelector('[data-lb-pair-payment-gate]');
      if (!gate) return;

      const variant = this.currentVariant();
      const options = this.variantOptions(variant);
      const committedColor = options[this.colorOptionIndex] || '';
      const committedLens = options[this.lensOptionIndex] || '';
      const showPayment = !this.pending
        && mode === 'primary'
        && Boolean(variant)
        && variant?.available !== false
        && committedColor === normalize(color)
        && committedLens === normalize(this.primaryLensValue);

      gate.hidden = !showPayment;
      gate.toggleAttribute('inert', !showPayment);
      gate.toggleAttribute('aria-hidden', !showPayment);
    }

    setPdpStatus(message, isError = false) {
      this.currentTriggers().forEach((trigger) => {
        const status = trigger.querySelector('[data-lb-pair-pdp-status]');
        if (!status) return;
        status.textContent = message || '';
        status.classList.toggle('is-error', Boolean(message && isError));
      });
    }

    currentPicker() {
      const productInfo = this.form?.closest('.product-info') || this.productInfo;
      return productInfo?.querySelector(`variant-picker[form-id="${CSS.escape(this.formId)}"]`) || null;
    }

    currentVariant() {
      const idInput = this.form?.querySelector('[name="id"]');
      const id = Number(idInput?.value || 0);
      return (this.product?.variants || []).find((variant) => Number(variant.id) === id) || null;
    }

    nativeOptionControl(position) {
      return Array.from(this.form?.elements || []).find((element) => {
        return element.matches?.('input[data-option-position]:checked')
          && Number(element.dataset.optionPosition) === Number(position);
      }) || null;
    }

    nativeOptionLabel(control) {
      return normalize(
        control?.dataset?.optionLabel
        || control?.labels?.[0]?.textContent
        || control?.closest('label')?.textContent
      );
    }

    variantOptions(variant) {
      if (!variant) return [];
      if (Array.isArray(variant.options)) return variant.options.map(normalize);

      return [variant.option1, variant.option2, variant.option3].filter((value) => value != null).map(normalize);
    }

    findVariant(color, lensValue) {
      return (this.product?.variants || []).find((variant) => {
        const values = this.variantOptions(variant);
        return values[this.colorOptionIndex] === color && values[this.lensOptionIndex] === lensValue;
      }) || null;
    }

    variantForStagedSelection() {
      const lensValue = this.stagedMode === 'primary' ? this.primaryLensValue : this.rxLensValue;
      return this.findVariant(this.stagedColor, lensValue);
    }

    modeAvailable(mode) {
      const lensValue = mode === 'primary' ? this.primaryLensValue : this.rxLensValue;
      const variant = this.findVariant(this.stagedColor, lensValue);
      return Boolean(variant && variant.available !== false);
    }

    requestedMode() {
      const requested = new URL(window.location.href).searchParams.get('lens');
      return ['primary', 'readers', 'prescription'].includes(requested) ? requested : '';
    }

    reconcileRequestedMode() {
      const requested = this.requestedMode();
      if (!requested || !this.modeAvailable(requested)) return;

      const variant = this.currentVariant();
      const lens = this.variantOptions(variant)[this.lensOptionIndex];
      const targetLens = requested === 'primary' ? this.primaryLensValue : this.rxLensValue;
      const modeMatches = requested === 'primary'
        ? this.builder.mode === 'primary'
        : this.builder.mode === requested;

      if (lens === targetLens && modeMatches) return;
      window.queueMicrotask(() => this.selectPdpMode(requested, { silent: true }));
    }

    syncStagedSelection({ preserveMode = false, preferRequested = false } = {}) {
      const variant = this.currentVariant();
      const values = this.variantOptions(variant);
      const colorInput = this.nativeOptionControl(this.colorOptionPosition);
      const currentColor = this.nativeOptionLabel(colorInput) || values[this.colorOptionIndex] || this.stagedColor || '';
      const currentLens = values[this.lensOptionIndex];
      const currentMode = currentLens === this.rxLensValue && ['readers', 'prescription'].includes(this.builder.mode)
        ? this.builder.mode
        : 'primary';
      const requestedMode = preferRequested ? this.requestedMode() : '';
      this.stagedColor = currentColor;
      this.stagedColorOptionValueId = colorInput?.value || this.stagedColorOptionValueId;
      this.stagedMode = preserveMode && this.stagedMode
        ? this.stagedMode
        : requestedMode || currentMode;
      this.updateLensAvailability({ chooseFallback: true });
      this.updatePreview();
      this.updateActions();
      this.updatePdpControls();
    }

    async selectPdpMode(mode, { silent = false } = {}) {
      if (this.pending || !['primary', 'readers', 'prescription'].includes(mode)) return;

      const previousMode = this.stagedMode || 'primary';
      this.setStatus('');
      this.setPdpStatus('');
      this.stagedMode = mode;
      this.updateLensAvailability({ chooseFallback: true });

      if (this.stagedMode !== mode || !this.modeAvailable(mode)) {
        this.stagedMode = previousMode;
        this.updateLensAvailability({ chooseFallback: true });
        this.updatePreview();
        this.updateActions();
        this.updatePdpControls();
        if (!silent) this.setPdpStatus('That lens option is not available in this color.', true);
        return;
      }

      this.updatePreview();
      this.updateActions();
      this.updatePdpControls();
      this.setBusy(true);

      try {
        await this.commitSelection({
          color: this.stagedColor,
          colorOptionValueId: this.stagedColorOptionValueId,
          mode
        }, { markCommitted: false });
        this.setBusy(false);
        this.updatePdpControls();
      } catch (error) {
        this.stagedMode = previousMode;
        this.builder.applyMode(previousMode, { syncNative: false, updateUrl: true });
        this.syncStagedSelection({ preserveMode: true });
        this.setBusy(false);
        if (!silent) this.setPdpStatus(error.message || 'That lens option could not be selected.', true);
      }
    }

    async handlePdpAction() {
      if (this.pending) return;

      const mode = this.stagedMode || 'primary';
      if (!this.modeAvailable(mode)) {
        this.setPdpStatus('Choose an available lens option.', true);
        return;
      }

      this.setStatus('');
      this.setPdpStatus('');
      this.setBusy(true);

      try {
        await this.commitSelection({
          color: this.stagedColor,
          colorOptionValueId: this.stagedColorOptionValueId,
          mode
        }, { markCommitted: false });

        if (mode === 'primary') {
          const submit = this.form.querySelector('buy-buttons button[type="submit"]');
          if (!submit || submit.disabled) throw new Error('This pair is not available right now.');
          this.form.requestSubmit(submit);
          return;
        }

        this.setBusy(false);
        await this.drawer.show();
      } catch (error) {
        this.setBusy(false);
        this.setPdpStatus(error.message || 'This pair could not be selected.', true);
      }
    }

    updateLensAvailability({ chooseFallback = false } = {}) {
      const rxAvailable = this.modeAvailable('readers');
      const primaryAvailable = this.modeAvailable('primary');

      this.querySelectorAll('[data-lb-pair-lens]').forEach((input) => {
        const available = input.value === 'primary' ? primaryAvailable : rxAvailable;
        input.disabled = !available;
        input.closest('.lb-build-pair__lens-choice')?.classList.toggle('is-disabled', !available);
      });

      if (chooseFallback && !this.modeAvailable(this.stagedMode)) {
        this.stagedMode = 'primary';
      }

      const selectedLens = this.querySelector(`[data-lb-pair-lens][value="${CSS.escape(this.stagedMode)}"]`);
      if (selectedLens && !selectedLens.disabled) selectedLens.checked = true;

      const availability = this.querySelector('[data-lb-pair-availability]');
      if (availability) {
        availability.hidden = rxAvailable;
        availability.textContent = rxAvailable
          ? ''
          : `${this.stagedColor} is available in ${this.modeLabel('primary')} only.`;
      }
    }

    updatePreview() {
      const variant = this.variantForStagedSelection()
        || this.findVariant(this.stagedColor, this.primaryLensValue)
        || this.currentVariant();
      const image = variant?.featured_image?.src
        || variant?.featured_media?.preview_image?.src
        || variant?.featured_media?.src
        || null;
      const preview = this.querySelector('[data-lb-pair-preview] img');

      if (preview instanceof HTMLImageElement && image) {
        preview.removeAttribute('srcset');
        preview.removeAttribute('sizes');
        preview.src = this.imageUrl(image, 1400);
        preview.alt = `${this.dataset.productTitle} glasses in ${this.stagedColor}`;
      }

      const selection = this.querySelector('[data-lb-pair-selection]');
      if (selection) selection.textContent = `${this.stagedColor} · ${this.modeLabel(this.stagedMode)}`;
    }

    imageUrl(source, width) {
      try {
        const url = new URL(source, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (error) {
        return source;
      }
    }

    modeLabel(mode) {
      if (mode === 'primary' && this.primaryLensValue === 'Sunglasses') return 'Sunglasses';
      return MODE_LABELS[mode] || MODE_LABELS.primary;
    }

    setStage(step, { focus = true } = {}) {
      this.step = 1;
      this.querySelectorAll('[data-lb-pair-stage]').forEach((stage) => {
        stage.hidden = Number(stage.dataset.lbPairStage) !== 1;
      });

      this.updateActions();

      if (focus) {
        const heading = this.querySelector(`[data-lb-pair-stage="${this.step}"] .lb-build-pair__heading`);
        window.requestAnimationFrame(() => heading?.focus({ preventScroll: false }));
      }
    }

    updateActions() {
      if (!this.nextButton) return;
      this.nextButton.textContent = this.stagedMode === 'primary' ? 'ADD TO CART' : 'CONTINUE';
      this.nextButton.disabled = this.pending || !this.modeAvailable(this.stagedMode);
    }

    goNext() {
      if (this.pending) return;
      this.setStatus('');
      this.confirmSelection();
    }

    async confirmSelection() {
      if (!this.modeAvailable(this.stagedMode)) {
        this.setStatus('Choose an available lens option.', true);
        return;
      }

      const selection = {
        color: this.stagedColor,
        colorOptionValueId: this.stagedColorOptionValueId,
        mode: this.stagedMode
      };
      const openRevision = this.openRevision;
      this.setBusy(true);

      try {
        await this.commitSelection(selection);

        if (!this.isCurrentOpen(openRevision)) {
          this.setBusy(false);
          return;
        }

        if (selection.mode === 'primary') {
          const submit = this.form.querySelector('buy-buttons button[type="submit"]');
          if (!submit || submit.disabled) throw new Error('This pair is not available right now.');
          this.form.requestSubmit(submit);
          return;
        }

        this.showConfiguration(selection);
        this.setBusy(false);
      } catch (error) {
        if (!this.isCurrentOpen(openRevision)) {
          this.setBusy(false);
          return;
        }

        this.setBusy(false);
        this.setStatus(error.message || 'This pair could not be configured.', true);
      }
    }

    async commitSelection(selection, { markCommitted = true } = {}) {
      const lensValue = selection.mode === 'primary' ? this.primaryLensValue : this.rxLensValue;
      const targetVariant = this.findVariant(selection.color, lensValue);
      if (!targetVariant || targetVariant.available === false) {
        throw new Error('That combination is not available.');
      }

      const picker = this.currentPicker();
      if (!picker || typeof picker.selectCombination !== 'function') {
        throw new Error('The product selector is still loading. Try again.');
      }

      const optionValueIds = new Map();
      Array.from(this.form.elements).forEach((element) => {
        if (!element.matches?.('input[data-option-position]:checked')) return;
        optionValueIds.set(Number(element.dataset.optionPosition), element.value);
      });

      optionValueIds.set(this.colorOptionPosition, selection.colorOptionValueId);
      optionValueIds.set(
        this.lensOptionPosition,
        selection.mode === 'primary'
          ? this.dataset.primaryLensOptionValueId
          : this.dataset.rxLensOptionValueId
      );

      const optionValues = Array.from(optionValueIds.entries())
        .sort((a, b) => a[0] - b[0])
        .map((entry) => entry[1]);

      if (optionValues.length !== (this.product.options || []).length || optionValues.some((value) => !value)) {
        throw new Error('That combination could not be confirmed.');
      }

      const currentId = Number(this.form.querySelector('[name="id"]')?.value || 0);
      if (currentId !== Number(targetVariant.id)) {
        await picker.selectCombination({ optionValues, productChange: false });
      }

      const committedId = Number(this.form.querySelector('[name="id"]')?.value || 0);
      if (committedId !== Number(targetVariant.id)) {
        throw new Error('That combination could not be confirmed.');
      }

      if (markCommitted) this.committedThisOpen = true;
      this.stagedColor = selection.color;
      this.stagedColorOptionValueId = selection.colorOptionValueId;
      this.stagedMode = selection.mode;
      this.builder.applyMode(selection.mode, { syncNative: false, updateUrl: true });
      this.refreshPdp();
    }

    isCurrentOpen(openRevision) {
      return this.openRevision === openRevision && this.drawer?.hasAttribute('open');
    }

    showConfiguration(selection, { focus = true } = {}) {
      this.querySelectorAll('[data-lb-pair-stage]').forEach((stage) => {
        stage.hidden = true;
      });
      this.configuration.hidden = false;
      this.drawer.setAttribute('data-lb-pair-config-active', '');

      const summary = this.querySelector('[data-lb-pair-configuration-summary]');
      if (summary) summary.textContent = `${selection.color} · ${this.modeLabel(selection.mode)}`;

      const heading = this.querySelector('[data-lb-pair-configuration-title]');
      if (heading) {
        heading.textContent = selection.mode === 'readers'
          ? 'Choose your reader strength.'
          : selection.mode === 'prescription'
            ? 'Choose your prescription type.'
            : 'Finish your lenses.';
      }

      if (focus) {
        window.requestAnimationFrame(() => {
          heading?.focus({ preventScroll: false });
          heading?.scrollIntoView({ block: 'start' });
        });
      }
    }

    hideConfiguration() {
      if (this.configuration) this.configuration.hidden = true;
      this.drawer?.removeAttribute('data-lb-pair-config-active');
    }

    leaveConfiguration() {
      if (this.pending) return;
      this.hideConfiguration();
      this.syncStagedSelection({ preserveMode: true });
      this.setStage(1);
    }

    setBusy(busy) {
      this.pending = busy;
      [this.nextButton].forEach((button) => {
        if (!button) return;
        button.toggleAttribute('aria-busy', busy);
        button.disabled = busy;
      });

      this.querySelectorAll('[data-lb-pair-lens]').forEach((input) => {
        input.disabled = busy;
      });

      this.currentTriggers().forEach((trigger) => {
        trigger.querySelectorAll(`[aria-controls="${CSS.escape(this.drawer.id)}"]`).forEach((control) => {
          control.disabled = busy;
        });
      });

      this.updatePdpControls();

      if (!busy) {
        this.updateLensAvailability({ chooseFallback: false });
        this.updateActions();
        this.updatePdpControls();
      }
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.classList.toggle('is-error', Boolean(message && isError));
    }
  }

  if (!window.customElements.get('lb-build-pair')) {
    window.customElements.define('lb-build-pair', BuildPair);
  }
})();
